import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner, FaDollarSign } from 'react-icons/fa';

const API_URL = '/api';

const initialFormData = {
  currency_code: '',
  currency_name: '',
  symbol: '',
  default_rate: '1.0',
  status: 'Active', // Should be converted to boolean/bit in backend
  effective_date: '',
  created_by: 1, // Placeholder, will be replaced by actual user ID
};

const CurrencyFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);

  // Fetch current user
  useEffect(() => {
    fetch(`${API_URL}/current-user`)
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setCurrentUser(data.user);
        }
      })
      .catch(error => {
        console.error('Error fetching current user:', error);
      });
  }, []);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({
          currency_code: initialData.CurrencyCode || '',
          currency_name: initialData.CurrencyName || '',
          symbol: initialData.Symbol || '',
          default_rate: initialData.DefaultRate?.toString() || '1.0',
          // Backend returns boolean, form uses string for select
          status: (initialData.Status === true || initialData.Status === 1) ? 'Active' : 'Inactive',
          effective_date: initialData.EffectiveDate ? new Date(initialData.EffectiveDate).toISOString().split('T')[0] : '',
          created_by: initialData.CreatedBy || 1,
        });
      } else {
        // Only fetch new code if creating and code is empty or default
        if (!formData.currency_code || formData.currency_code === initialFormData.currency_code) {
          fetchNextCode();
        }
        setFormData(prev => ({
          ...prev,
          effective_date: new Date().toISOString().split('T')[0],
          status: 'Active',
          created_by: currentUser?.UserID || 1,
        }));
      }
    }
  }, [open, initialData, isEdit, currentUser]); // Added currentUser to dependencies

  const fetchNextCode = async () => {
    setIsLoadingCode(true);
    try {
      const response = await fetch(`${API_URL}/currencylatestcode`);
      const data = await response.json();
      setFormData(prev => ({ ...prev, currency_code: data.nextCode }));
    } catch (error) {
      console.error('Error fetching next currency code:', error);
    } finally {
      setIsLoadingCode(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const dataToSend = {
      ...formData,
      // Convert status string back to boolean/bit for backend if needed, or let backend handle it
      status: formData.status === 'Active' ? 1 : 0, 
      created_by: currentUser?.UserID || 1,
      updated_by: currentUser?.UserID || 1, // Add updated_by for consistency
    };
    onSubmit(dataToSend);
  };

  if (!open) return null;

  return (
  <div className="fixed inset-0 z-50 flex rounded-2xl items-center justify-center bg-black bg-opacity-40 p-4">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full p-4 md:p-8 relative overflow-y-auto max-h-[90vh]" style={{ maxWidth: '80%' }}>
      <button className="absolute top-4 right-4 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}><FaTimes size={20} /></button>
      <h2 className="text-xl md:text-2xl font-bold text-[#2b6777] mb-4 md:mb-6 text-center">{isEdit ? 'Edit Currency' : 'Create New Currency'}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Currency Information */}
        <div className='bg-white p-4 rounded-xl shadow-md'>
          <h3 className="text-lg font-bold text-[#2b6777] mb-2">Currency Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className='flex flex-col md:flex-row gap-2 justify-between items-center'>
              <label className="block w-full md:w-1/4 text-sm font-medium text-[#2b6777] text-center md:text-left whitespace-nowrap">Currency Code</label>
              <input
                type="text"
                value={isLoadingCode ? 'Loading...' : formData.currency_code}
                onChange={(e) => handleInputChange('currency_code', e.target.value)}
                className="w-full md:w-3/4 border rounded px-3 py-2 bg-gray-100 text-gray-700"
                placeholder="e.g., EUR, USD, INR (or auto-generated)"
                required
                readOnly
              />
            </div>
            <div className='flex flex-col md:flex-row gap-2 justify-between items-center'>
              <label className="block w-full md:w-1/4 text-sm font-medium text-[#2b6777] text-center md:text-left whitespace-nowrap">Currency Name *</label>
              <input
                type="text"
                value={formData.currency_name}
                onChange={(e) => handleInputChange('currency_name', e.target.value)}
                className="w-full md:w-3/4 border rounded px-3 py-2 border-[#c8d8e4]"
                placeholder="e.g., US Dollar"
                required
              />
            </div>
            <div className='flex flex-col md:flex-row gap-2 justify-between items-center'>
              <label className="block w-full md:w-1/4 text-sm font-medium text-[#2b6777] text-center md:text-left whitespace-nowrap">Symbol *</label>
              <input
                type="text"
                value={formData.symbol}
                onChange={(e) => handleInputChange('symbol', e.target.value)}
                className="w-full md:w-3/4 border rounded px-3 py-2 border-[#c8d8e4]"
                placeholder="e.g., $"
                required
                maxLength={5}
              />
            </div>
            <div className='flex flex-col md:flex-row gap-2 justify-between items-center'>
              <label className="block w-full md:w-1/4 text-sm font-medium text-[#2b6777] text-center md:text-left whitespace-nowrap">Default Rate</label>
              <input
                type="number"
                step="0.0001"
                value={formData.default_rate}
                onChange={(e) => handleInputChange('default_rate', e.target.value)}
                className="w-full md:w-3/4 border rounded px-3 py-2 border-[#c8d8e4]"
                placeholder="1.0000"
                min="0"
              />
            </div>
            <div className='flex flex-col md:flex-row gap-2 justify-between items-center'>
              <label className="block w-full md:w-1/4 text-sm font-medium text-[#2b6777] text-center md:text-left whitespace-nowrap">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full md:w-3/4 border rounded px-3 py-2 border-[#c8d8e4]"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className='flex flex-col md:flex-row gap-2 justify-between items-center'>
              <label className="block w-full md:w-1/4 text-sm font-medium text-[#2b6777] text-center md:text-left whitespace-nowrap">Effective Date</label>
              <input
                type="date"
                value={formData.effective_date}
                onChange={(e) => handleInputChange('effective_date', e.target.value)}
                className="w-full md:w-3/4 border rounded px-3 py-2 border-[#c8d8e4]"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-end gap-4 mt-6">
          <button type="button" className="px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold w-full md:w-auto" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Currency' : 'Create Currency')}
          </button>
        </div>
      </form>
    </div>
  </div>
);
};

export default CurrencyFormModal; 